import type { Task } from '../db/models'

/*
 * Die Regeln von **Demnächst**, geteilt mit der Mobile-App (ADR 0036, `apps/mobile/src/shared/desktop.ts`).
 * Hierher gezogen aus `UpcomingPage/hooks/upcomingTasks.utils.ts`, das sie unverändert weiterreicht.
 */

/** Effective date used for Upcoming grouping: startDate takes precedence, else dueDate. */
export function effectiveDate(task: Task): Date | null {
  return task.startDate ?? task.dueDate ?? null
}


/**
 * Reihenfolge innerhalb EINES Tages:
 *  1. `dayOrder` aufsteigend — die von Hand gezogene Position. `null` (nie
 *     gezogen) kommt hinter alles Gezogene.
 *  2. `startTime` aufsteigend, `null` zuletzt.
 *  3. `order` als letzter Entscheider.
 *
 * **Ziehen gewinnt** (Abnahme F-DN1, 22.09.2026, Entscheidung des Nutzers).
 * Bis dahin sortierte die Uhrzeit zuerst und `order` entschied nur den
 * Gleichstand — ein Ziehen innerhalb des Tages blieb damit wirkungslos.
 *
 * Solange niemand gezogen hat, sind alle `dayOrder` gleich `null` und es
 * entscheiden Regel 2 und 3: genau die Reihenfolge von vorher. Es gibt darum
 * keine Migration und keinen Bruch für bestehende Daten — und eine Zeile ohne
 * gezogene Position ordnet sich weiterhin nach ihrer Uhrzeit ein.
 */
export function sortTasksInDay(tasks: Task[]): Task[] {
  return tasks.toSorted((a, b) => {
    const ad = a.dayOrder
    const bd = b.dayOrder
    if (ad !== bd) {
      if (ad == null) return 1
      if (bd == null) return -1
      return ad - bd
    }
    const at = a.startTime
    const bt = b.startTime
    if (at !== bt) {
      if (at == null) return 1
      if (bt == null) return -1
      return at.localeCompare(bt)
    }
    return a.order - b.order
  })
}


/**
 * Filter tasks for Upcoming rendering:
 *  - not soft-deleted
 *  - has an effective date (startDate or dueDate) — paused tasks stay (shown dormant, F-DN4)
 *  - belongs to current life space (via projectId ∈ lifeSpaceProjectIds, OR `task.lifeSpace` without project)
 *  - not completed — except ids in `graceIds` (kept visible for ~2s after toggle)
 */
export function filterUpcomingTasks({
  tasks,
  lifeSpace,
  lifeSpaceProjectIds,
  graceIds,
}: {
  tasks: Task[]
  lifeSpace: 'personal' | 'work' | 'home'
  lifeSpaceProjectIds: Set<string>
  graceIds: Set<string>
}): Task[] {
  return tasks.filter(task => {
    if (task.deletedAt) return false
    /*
      **Ruhende Aufgaben mit Datum stehen hier** (Abnahme F-DN4, 21.09.2026).
      Hier stand `if (task.pausedAt != null) return false` — die Regel lautete:
      Ruhendes gehört auf Ruhend. Der Nutzer hat sie gekippt: Wer ein „Wann"
      gesetzt hat, will die Aufgabe an ihrem Tag sehen, auch wenn sie ruht. Sie
      steht gedämpft da (`taskToEntryData` setzt `dormant`), und `R` legt von hier
      aus schlafen oder weckt auf.

      Das Datum bleibt die Bedingung: Ohne eines fällt sie eine Zeile tiefer
      ohnehin heraus — sie hätte keinen Tag.
    */
    if (!effectiveDate(task)) return false

    // Orphan tasks (no project) surface only in their own lifespace, read
    // from `task.lifeSpace` (required since PR 1 of the lifeSpace migration).
    // Before PR 1 the convention was "orphans = personal only"; after PR 2
    // Kurz-notiert is the primary orphan source per lifespace.
    if (task.projectId == null) {
      if (task.lifeSpace !== lifeSpace) return false
    } else if (!lifeSpaceProjectIds.has(task.projectId)) {
      return false
    }

    if (task.status === 'completed' && !graceIds.has(task.id)) return false

    return true
  })
}
